import { Injectable } from "@angular/core";
import { DataAccess, HttpGetRequestOptions, HttpPostRequestOptions } from "../../dataaccess/data.access";
import { OrganizationService } from "../../services/organization.service";
import { ContactService } from "../../services/contact.service";
import { EventType, EventTypeService } from "../../services/master.definitions/event.misc/event.type.service";
import { SearchCriteriaService } from "../../services/search/search-criteria/search.criteria.service";
import { NameService } from "../../services/name.service";
import { Item } from "../../pojo/Item";
import { S25Util } from "../../util/s25-util";
import type { AttachmentItem } from "../../services/attachment.service";
import type { DropDownItem } from "../../pojo/DropDownItem";
import { Cache, Invalidate } from "../../decorators/cache.decorator";
import {
    FieldType,
    IngestionPointCategory,
    IngestionPointFormUse,
    JSON_FIELD_TYPES,
    NameResolvableFieldType,
} from "./data.ingestion.const";
export type { FieldType } from "./data.ingestion.const";
import type { Workflow } from "../workflow/service/Workflow";
import type { Proto } from "../../pojo/Proto";
import type { Task } from "../../pojo/Task";
import type { DBRegistrationConfig } from "../student-engagement/attendance/attendance.service";
import { IngestionAccess } from "./ingestion.access";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { S25Dateformat } from "../s25-dateformat/s25.dateformat.service";
import { matchSourceAnswersToTargetFields } from "./ingestion.fork.field-match";
import { EmailAttachment } from "../../services/email.service";
import { S25ItemI } from "../../pojo/S25ItemI";

export interface IngestionPoint {
    ingestionPointId?: number;
    ingestionPointName?: string;
    category?: IngestionPointCategory;
    subCat?: string;
    description?: string;
    /** Rich HTML shown after successful submission; mirrors {@code success_message}. */
    successMessage?: string;
    /** JSON for form-level appearance; mirrors {@code form_style}. */
    formStyle?: string;
    enabled?: boolean;
    authless?: boolean;
    instance?: string;
    createDate?: Proto.ISODateString;
    lastModDate?: Proto.ISODateString;
    lastModUser?: string;
    fieldDefinitions?: IngestionFieldDefinition[];
    workflowId?: number;
    defaultWorkflowId?: number;
    activeStartDate?: Proto.ISODateString;
    activeEndDate?: Proto.ISODateString;
    // Backend boolean getter is isActive(), which typically serializes as active
    active?: boolean;
    allowEditAfterSubmit?: boolean;
    deleted?: boolean;
    deletedDate?: Proto.ISODateString;
    deletedUser?: string;
    use?: IngestionPointFormUse;

    /** Owner contact id; mirrors {@code owner_cont_id}. */
    ownerContId?: number;
    /** Lineage when copied from another form; mirrors {@code parent_ingestion_point_id}. */
    parentIngestionPointId?: number;
    /** Normalized access-rule graph from API; mirrors {@code accessRules} on {@code IngestionPoint}. */
    accessRules?: IngestionAccess.IngestionPointAccessRule[];

    // Frontend convenience fields
    subCategoryName?: string;
}

// -------------------------------------------------------------------------
// Form access + batch result types — canonical definitions in ./ingestion.access
// -------------------------------------------------------------------------
export { IngestionAccess } from "./ingestion.access";

export type ErrorDoc = IngestionAccess.ErrorDoc;
export type InfoDoc = IngestionAccess.InfoDoc;
export type ResultsPojo = IngestionAccess.ResultsPojo;
export type ValidationErrorDoc = IngestionAccess.ValidationErrorDoc;
export type IngestionPointAccessTraceEntry = IngestionAccess.IngestionPointAccessTraceEntry;
export type IngestionPointAccessDecision = IngestionAccess.IngestionPointAccessDecision;
export type ItemResultDocArray<T> = IngestionAccess.ItemResultDocArray<T>;
export type ItemResultDoc<T> = IngestionAccess.ItemResultDoc<T>;
export type IngestionPointAccessRule = IngestionAccess.IngestionPointAccessRule;
export type IngestionPointAccessRuleAction = IngestionAccess.IngestionPointAccessRuleAction;
export type IngestionPointAccessRuleObject = IngestionAccess.IngestionPointAccessRuleObject;
export type IngestionPointAccessRuleRight = IngestionAccess.IngestionPointAccessRuleRight;
export type IngestionPointAccessRulesUpdateEntry = IngestionAccess.IngestionPointAccessRulesUpdateEntry;

export interface IngestionFieldDefinition {
    fieldDefinitionId?: number;
    ingestionPointId?: number;
    instance?: string;
    fieldLabel?: string;
    fieldType?: FieldType;
    answerType?: AnswerType;
    infoText?: string;
    /** When true and infoText is set, runtime form shows info text expanded by default. */
    infoTextExpanded?: boolean;
    options?: IngestionFieldOption[];
    sortOrder?: number;
    hidden?: boolean;
    required?: boolean;
    readOnly?: boolean;
    isDeleted?: boolean;
    minLength?: number;
    maxLength?: number;
    regexValid?: string;
    reportingFieldCode?: string;
    defaultValue?: string;
    /** JSON for field-type-specific UI (e.g. OBJECT_OCCURRENCE; CATEGORIES / ORG_CATEGORIES: categoryIds + singleSelect). */
    fieldConfig?: string;
    derived: boolean;
    requiresReapproval?: boolean;
    custAtrbId?: number;
    custAtrb?: { atrbDataType?: string; multiVal?: boolean };

    // Frontend convenience fields
    /** Set by mapAnswersToFields when answers from DB were mapped to this field. */
    answersMapped?: boolean;
    newAnswer?: {
        text?: string;
    };
    answers?: IngestionFieldOption[];
    singleAnswer?: IngestionFieldOption;
    specialFieldItem?: DropDownItem; // For EVENT_TYPE and other special field types
    jsonFieldItem?: ObjectOccurrenceData | RegistrantsData | CategoriesData | ContactRoleData;
    longAnswer?: string; // all TEXT (user input) fields store their raw answer in longAnswer
    answerId?: number;
    validationError?: string;
    showInfoText?: boolean;
    files?: AttachmentItem[]; // For FILE field types
    fieldCode?: string;
    defaultValueName?: string; // For ORGANIZATION, CONTACT, EVENT_TYPE fields to store resolved item name
    prevFieldLabel?: string; // For tracking previous field label for auto-generation logic
}

export interface IngestionFieldOption {
    fieldDefinitionId?: number;
    option?: string;
    ingestionPointId?: number;
    instance?: string;
    sortOrder?: number;
}

export interface IngestionFieldAnswer {
    answerId?: number;
    instance?: string;
    fieldDefinitionId?: number;
    contId?: number;
    ingestionPointId?: number;
    submissionId?: number;
    answerString?: string;
    answerLargeString?: string;
    answerInteger?: number;
    answerFloat?: number;
    answerBoolean?: boolean;
    answerDatetime?: string;
    answerJson?: string;
    answerRaw?: string;
    fieldDefinition?: IngestionFieldDefinition;
}

export interface ContactRoleData {
    items: { roleId: number; roleName?: string; contactId: number; contactName?: string }[];
}

/** Serialized `WorkflowAudit.WorkflowAuditType` enum names from the API (25saas). */
export type IngestionWorkflowAuditType =
    | "RESET"
    | "REQUEST_CHANGES"
    | "APPROVE"
    | "DENY"
    | "STAGE_TRANSITION"
    | "WORKFLOW_INITIALIZED"
    | "CANCEL"
    | "RESUBMISSION"
    | "ACTION_EXECUTED"
    | "WARNING"
    | "ERROR";

/** Workflow audit row from GET .../workflow-audit.json (institution datetimes converted for display). */
export interface IngestionWorkflowAuditRow {
    workflowAuditId?: number;
    workflowItemTypeId?: number;
    workflowItemId?: number;
    workflowProcessInstanceId?: number | null;
    auditType?: IngestionWorkflowAuditType;
    auditDate?: Proto.ISODateString;
    auditUser?: string;
    description?: string | null;
    previousValue?: string | null;
    newValue?: string | null;
    stageId?: number | null;
    stateId?: number | null;
    workflowProcessDisplayName?: string | null;
    stageDisplayName?: string | null;
    stateDisplayName?: string | null;
    /** Populated for range and per-submission workflow-audit APIs when known. */
    submissionIdUuid?: string | null;
    ingestionPointName?: string | null;
    /** When set, row may expand to read-only IngestionFormComponent. */
    ingestionPointId?: number;
    organizationId?: number | null;
    organizationDisplayName?: string | null;
}

export interface FormAssignmentRecipient {
    contId?: number;
    email?: string;
    firstName?: string;
    familyName?: string;
    /** Frontend-only — stripped before sending to backend */
    needsName?: boolean;
    /** Frontend-only — stripped before sending to backend */
    displayName?: string;
}

export interface FormAssignmentEmailsRequest {
    subject: string;
    body: string;
    recipients: {
        to: FormAssignmentRecipient[];
        cc: FormAssignmentRecipient[];
        bcc: FormAssignmentRecipient[];
    };
    /** Same attachment objects as null/mail.json (see EmailService.getAttachments). */
    attachments?: EmailAttachment[];
    /** Optional sender. Blank keeps the signed-in user. */
    from?: string;
}

export interface FormAssignmentEmailsResponse {
    error?: string;
}

export interface IngestionSubmission {
    submissionId?: number;
    submissionIdUuid?: string;
    itemTypeId?: number;
    itemId?: number;
    instance?: string;
    contId?: number;
    ingestionPointId?: number;
    createDate?: Proto.ISODateString;
    lastModDate?: Proto.ISODateString;
    submitted?: boolean;
    answers?: IngestionFieldAnswer[];
    submissionDate?: Proto.ISODateString;
    workflowProcessId?: number;
    workflowProcessInstanceId?: number;
    parentProcessInstanceId?: number;
    currentWorkflowStageId?: number;
    currentWorkflowStage?: Workflow.WorkflowStage; // only stageName is populated
    ingestionPoint?: IngestionPoint; // only ingestionPointName is populated
    todoId?: number; // only for approval / denial on todos
    workflowStatus?: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "DENIED" | "REQUESTED_CHANGES" | "CANCELED";
    lastModUser?: string;

    // Linked forms - populated on demand when viewing a form
    childrenItems?: Workflow.WorkflowItem[]; // Forms spawned from this form
    parent?: Workflow.WorkflowItem; // Parent form (null if root)

    eventId?: number;
    electionId?: number;

    // Frontend-only properties
    submitterName?: string; // Contact name for display
    showingRequestedChanges?: boolean; // UI state for showing/hiding requested changes
    requestedChangesList?: Task.DBTodo[]; // List of requested changes from todo comments
}

export enum AnswerType {
    STRING = "STRING",
    LARGE_STRING = "LARGE_STRING",
    INTEGER = "INTEGER",
    FLOAT = "FLOAT",
    BOOLEAN = "BOOLEAN",
    DATETIME = "DATETIME",
    FILE = "FILE",
    JSON = "JSON",
    VARIANT = "VARIANT",
    IMAGE = "IMAGE",
}

export namespace AnswerType {
    /** Maps a CustAtrb.atrbDataType character code to the corresponding AnswerType. */
    export function fromAtrbDataType(atrbDataType: string | undefined): AnswerType | undefined {
        switch (atrbDataType) {
            case "B":
                return AnswerType.BOOLEAN;
            case "N":
                return AnswerType.INTEGER;
            case "F":
                return AnswerType.FLOAT;
            case "S":
            case "R":
                return AnswerType.STRING;
            case "X":
                return AnswerType.LARGE_STRING;
            case "D":
            case "E":
            case "T":
            case "L":
                return AnswerType.DATETIME;
            case "I":
                return AnswerType.IMAGE;
            default:
                return undefined;
        }
    }
}

// Object Occurrence data structures (aligned with 25saas document.ingestion.ObjectOccurrence)
export interface ResourceOccurrence {
    resourceId: number;
    /** Requested quantity; null/undefined or non-positive treated as 1 server-side. */
    actRsrcCount?: number;
}

/** Matches ObjectOccurrence.ResourceOccurrence#getActRsrcCount() in 25saas. */
export function effectiveActRsrcCount(resource: ResourceOccurrence): number {
    const count = resource.actRsrcCount;
    return count != null && count > 0 ? count : 1;
}

export interface ObjectOccurrence {
    occurrenceUuid: string; // unique ID for this occurrence
    startDate: Proto.ISODateString; // ISO datetime string
    endDate: Proto.ISODateString; // ISO datetime string
    locationIds: number[]; // array of location item IDs
    resources: ResourceOccurrence[];
    comment?: string;
}

/** Ensures nested arrays exist after JSON parse (matches 25saas document shape). */
export function normalizeObjectOccurrence(occ: ObjectOccurrence): ObjectOccurrence {
    return {
        occurrenceUuid: occ.occurrenceUuid,
        startDate: occ.startDate,
        endDate: occ.endDate,
        locationIds: occ.locationIds ?? [],
        resources: (occ.resources ?? []).map((resource) => ({
            resourceId: resource.resourceId,
            actRsrcCount: effectiveActRsrcCount(resource),
        })),
        comment: occ.comment,
    };
}

export function normalizeObjectOccurrenceData(
    data: ObjectOccurrenceData | undefined,
): ObjectOccurrenceData | undefined {
    if (!data?.occurrences?.length) {
        return data;
    }
    return {
        occurrences: data.occurrences.map((entry) => normalizeObjectOccurrence(entry)),
    };
}

export interface ObjectOccurrenceData {
    occurrences: ObjectOccurrence[];
}

export interface RegistrantsData {
    type: DBRegistrationConfig["registrantType"];
    items: { itemId: number; itemName?: string }[];
}

export interface CategoriesData {
    items: { itemId: number; itemName?: string }[];
}

export interface EventSurveyConfig {
    eventId: number;
    ingestionPointId?: number;
    scheduledEmailId?: number;
    scheduledEmail?: {
        daysFromEventEnd?: number;
        scheduledEmailId?: number;
        isEventSurvey?: boolean;
    };
}

export interface ReportingFieldInfo {
    reportingFieldCode: string;
    answerType: AnswerType;
    fieldType?: FieldType;
    dateModifier?: DateModifier; // Frontend convenience property for group by operations
}

export interface SearchRequest {
    ingestionPointIds?: number[];
    searchAllForms: boolean;
    projectionFields?: string[];
    predicate?: any;
    pageNum?: number;
    pageSize?: number;
    sorts?: string[];
    pageKey?: string;
    organizationIds?: number[];
    eventIds?: number[];
}

export enum DateModifier {
    YEAR = "YEAR",
    MONTH_YEAR = "MONTH_YEAR",
    DAY_MONTH_YEAR = "DAY_MONTH_YEAR",
    DAY_OF_WEEK = "DAY_OF_WEEK",
}

export interface ReportRequest {
    ingestionPointIds?: number[];
    searchAllForms: boolean;
    groupByFields?: ReportingFieldInfo[];
    aggregations?: AggregationSpec[];
    predicate?: any;
    includeForm?: boolean;
    organizationIds?: number[];
    eventIds?: number[];
}

export interface AggregationSpec {
    type: "COUNT" | "COUNT_DISTINCT" | "SUM" | "AVG";
    field: string;
}

export interface BaseResponse<T = unknown> {
    data: T[];
}

export interface SearchResponse extends BaseResponse<IngestionSubmission> {
    total: number;
    totalPages: number;
    pageNum: number;
    pageSize: number;
    pageCacheKey?: string;
    fastFirstPage: boolean;
    error?: string;
    errorCode?: string;
}

export interface ReportResponse extends BaseResponse<{ [key: string]: string | number }> {
    err_msg?: string;
    err_code?: string;
}

export interface SaveSearchRequest {
    name: string;
    searchRequest: SearchRequest;
}

export interface SaveReportRequest {
    name: string;
    reportRequest: ReportRequest;
}

export interface IngestionSavedQuery {
    queryId: number;
    queryName: string;
    queryType: string;
    createdBy: string;
    createdDate: string; // ISO date string from LocalDateTime
}

export interface SavedQueryWithData extends IngestionSavedQuery {
    searchRequest?: SearchRequest;
    reportRequest?: ReportRequest;
}

export interface DistinctFieldsRequest {
    ingestionPointIds?: number[];
    searchAllForms: boolean;
    includeDeleted?: boolean;
}

@Injectable({ providedIn: "root" })
export class DataIngestionService {
    // Private helper method to initialize frontend convenience fields for field definitions
    private static initializeFieldDefinition(fieldDef: IngestionFieldDefinition): void {
        // Initialize prevFieldLabel for auto-generation logic
        fieldDef.prevFieldLabel = fieldDef.fieldLabel;
        if (S25Util.isUndefined(fieldDef.requiresReapproval)) {
            fieldDef.requiresReapproval = true;
        }
    }

    // Private helper method to initialize field definitions for ingestion points
    private static initializeFieldDefinitions(ingestionPoint: IngestionPoint): void {
        if (ingestionPoint.fieldDefinitions) {
            ingestionPoint.fieldDefinitions.forEach((fieldDef) => this.initializeFieldDefinition(fieldDef));
        }
    }

    // Ingestion Point Management
    public static async getIngestionPoints(
        category?: IngestionPointCategory,
        subCategory?: string,
        includeDeleted?: boolean,
        /** When set, only points where `use` has at least one of these flags true (OR). */
        formUse?: Array<keyof NonNullable<IngestionPoint["use"]>>,
        permissionFilterAction?: IngestionAccess.IngestionPointAction,
    ): Promise<IngestionPoint[]> {
        let url = "/ingestion/points.json";
        const params = new URLSearchParams();

        if (category) {
            params.append("category", category);
        }
        if (subCategory) {
            params.append("subCategory", subCategory);
        }
        if (S25Util.isDefined(includeDeleted)) {
            params.append("includeDeleted", includeDeleted!.toString());
        }
        if (formUse?.length) {
            for (const useKey of formUse) {
                params.append("formUse", useKey);
            }
        }
        if (permissionFilterAction != null) {
            params.append("action", permissionFilterAction);
        }

        if (params.toString()) {
            url += "?" + params.toString();
        }

        const ingestionPoints = (await DataAccess.get<IngestionPoint[]>(url)) || [];
        // Initialize field definitions for all ingestion points
        ingestionPoints.forEach((point) => this.initializeFieldDefinitions(point));
        return ingestionPoints;
    }

    /**
     * Batch load normalized access rules (caller must be allowed to manage each form).
     * GET {@code /ingestion/points/access.json?ids=1+2+3}
     */
    public static async getAccessRulesForPoints(
        ingestionPointIds: number[],
    ): Promise<IngestionPointAccessRulesUpdateEntry[]> {
        if (!ingestionPointIds?.length) {
            return [];
        }
        const idsParam = ingestionPointIds.map(String).join("+");
        const qs = DataAccess.params({ ids: idsParam });
        const path = qs ? `/ingestion/points/access.json?${qs}` : `/ingestion/points/access.json`;
        const rules = (await DataAccess.get<IngestionPointAccessRulesUpdateEntry[]>(path)) || [];

        const expensiveItems: S25ItemI.NumId[] = [];

        for (const rule of rules) {
            for (const accessRule of rule.accessRules || []) {
                for (const object of accessRule.objects || []) {
                    if (object.objectType === IngestionAccess.AccessRuleObjectType.ACCOUNT_ID) {
                        expensiveItems.push({ itemTypeId: Item.Ids.Organization, itemId: Number(object.objectId) });
                    }
                    if (object.objectType === IngestionAccess.AccessRuleObjectType.CONT_ID) {
                        expensiveItems.push({ itemTypeId: Item.Ids.Contact, itemId: Number(object.objectId) });
                    }
                }
            }
        }

        const expensiveItemMap = await NameService.getNames(expensiveItems);

        for (const rule of rules) {
            for (const accessRule of rule.accessRules || []) {
                for (const object of accessRule.objects || []) {
                    if (object.objectType === IngestionAccess.AccessRuleObjectType.ACCOUNT_ID) {
                        object.objectName = expensiveItemMap[`${object.objectId}-${Item.Ids.Organization}`];
                    }
                    if (object.objectType === IngestionAccess.AccessRuleObjectType.CONT_ID) {
                        object.objectName = expensiveItemMap[`${object.objectId}-${Item.Ids.Contact}`];
                    }
                }
            }
        }
        return rules;
    }

    /**
     * Replace access rules for one or more forms (full replace per form).
     * PUT {@code /ingestion/points/access.json}
     */
    public static async putAccessRulesForPoints(
        entries: IngestionPointAccessRulesUpdateEntry[],
    ): Promise<IngestionPointAccessRulesUpdateEntry[]> {
        const body: IngestionAccess.IngestionPointAccessRulesUpdateEntryListRoot = {
            List: entries,
        };
        return (
            (await DataAccess.put<IngestionPointAccessRulesUpdateEntry[]>("/ingestion/points/access.json", body)) || []
        );
    }

    public static async getIngestionPoint(
        ingestionPointId: number,
        options?: HttpGetRequestOptions,
    ): Promise<IngestionPoint | undefined> {
        const ingestionPoint = await DataAccess.get<IngestionPoint>(
            `/ingestion/point/${ingestionPointId}.json`,
            options,
        );
        if (!ingestionPoint) return undefined;
        this.initializeFieldDefinitions(ingestionPoint);
        return ingestionPoint;
    }

    public static async getIngestionPointName(ingestionPointId: number, options?: HttpGetRequestOptions) {
        const form = await DataIngestionService.getIngestionPoint(ingestionPointId, options);
        return form?.ingestionPointName;
    }

    public static async getIngestionPointNames(ids: number[]): Promise<Map<number, string>> {
        if (!ids.length) return new Map();
        const points = (await DataAccess.post<IngestionPoint[]>("/ingestion/point-names.json", ids)) || [];
        const map = new Map<number, string>();
        for (const p of points) {
            if (p.ingestionPointId != null && p.ingestionPointName) {
                map.set(p.ingestionPointId, p.ingestionPointName);
            }
        }
        return map;
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async createIngestionPoint(ingestionPoint: IngestionPoint): Promise<IngestionPoint> {
        // Set default category if not provided
        if (!ingestionPoint.category) {
            ingestionPoint.category = IngestionPointCategory.ENGAGEMENT;
        }
        return await DataAccess.post<IngestionPoint>("/ingestion/point.json", ingestionPoint);
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async updateIngestionPoint(
        ingestionPointId: number,
        ingestionPoint: IngestionPoint,
    ): Promise<IngestionPoint> {
        return await DataAccess.put<IngestionPoint>(`/ingestion/point/${ingestionPointId}.json`, ingestionPoint);
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async deleteIngestionPoint(ingestionPointId: number): Promise<void> {
        return await DataAccess.delete<void>(`/ingestion/point/${ingestionPointId}.json`);
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async undeleteIngestionPoint(ingestionPointId: number): Promise<void> {
        return await DataAccess.post<void>(`/ingestion/point/${ingestionPointId}/undelete.json`);
    }

    public static async populateSubCategoryNames(
        category: IngestionPointCategory,
        ingestionPoints: IngestionPoint[],
    ): Promise<void> {
        if (category === IngestionPointCategory.ENGAGEMENT) {
            await this.populateEngagementSubCategoryNames(ingestionPoints);
        }
        // Other categories not supported yet
    }

    private static async populateEngagementSubCategoryNames(ingestionPoints: IngestionPoint[]): Promise<void> {
        if (!ingestionPoints?.length) {
            return;
        }

        // Extract all subCat values and convert to integers
        const subCatIds = ingestionPoints
            .map((point) => point.subCat)
            .filter((subCat) => subCat)
            .map((subCat) => S25Util.parseInt(subCat || ""))
            .filter((id) => id !== 0);

        if (!subCatIds.length) {
            return;
        }

        // Get organization names for all subCat IDs
        const organizations = await OrganizationService.getOrganizationsMinimal(subCatIds);

        // Create a map of itemId -> organization object
        const orgMap = S25Util.fromEntries(organizations.map((org) => [org.itemId!, org]));

        // Set subCategoryName for each ingestion point
        ingestionPoints.forEach((point) => {
            if (point.subCat) {
                const orgId = S25Util.parseInt(point.subCat);
                const org = orgMap[orgId];
                if (org) {
                    point.subCategoryName = org.itemName;
                }
            }
        });
    }

    // Field Definition Management
    @Cache({ immutable: true, targetName: "DataIngestionService" })
    public static async getFieldDefinitions(ingestionPointId: number): Promise<IngestionFieldDefinition[]> {
        const fieldDefinitions =
            (await DataAccess.get<IngestionFieldDefinition[]>(`/ingestion/point/${ingestionPointId}/fields.json`)) ||
            [];
        // Initialize field definitions
        fieldDefinitions.forEach((fieldDef) => this.initializeFieldDefinition(fieldDef));
        return fieldDefinitions;
    }

    public static async getReportingFieldCodes(): Promise<string[]> {
        return (await DataAccess.get<string[]>("/ingestion/reporting-field-codes.json")) || [];
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async replaceFieldDefinitions(
        ingestionPointId: number,
        fieldDefinitions: IngestionFieldDefinition[],
    ): Promise<void> {
        return await DataAccess.post<void>(`/ingestion/point/${ingestionPointId}/fields.json`, fieldDefinitions);
    }

    // Submission Management
    public static async createSubmission(
        ingestionPointId: number,
        data?: Partial<IngestionSubmission>,
        httpOptions?: HttpPostRequestOptions,
    ): Promise<IngestionSubmission> {
        return await DataAccess.post<IngestionSubmission>(
            `/ingestion/point/${ingestionPointId}/submission.json`,
            data ?? {},
            httpOptions,
        );
    }

    public static async publicStartSubmission(
        ingestionPointId: number,
        identity: { firstName: string; familyName: string; email: string },
    ): Promise<IngestionSubmission> {
        return await DataAccess.post<IngestionSubmission>(
            `/ingestion/point/${ingestionPointId}/public-start.json`,
            identity,
        );
    }

    public static async sendFormAssignmentEmails(
        ingestionPointId: number,
        request: FormAssignmentEmailsRequest,
    ): Promise<FormAssignmentEmailsResponse> {
        return await DataAccess.post<FormAssignmentEmailsResponse>(
            `/ingestion/point/${ingestionPointId}/assignment-emails.json`,
            { FormAssignmentEmailsRequestDoc: request },
        );
    }

    public static async getSubmission(submissionIdUuid: string): Promise<IngestionSubmission> {
        return await DataAccess.get<IngestionSubmission>(`/ingestion/submission/${submissionIdUuid}.json`);
    }

    public static async getWorkflowAuditHistory(submissionIdUuid: string): Promise<IngestionWorkflowAuditRow[]> {
        const rows = await DataAccess.get<IngestionWorkflowAuditRow[]>(
            `/ingestion/submission/${submissionIdUuid}/workflow-audit.json`,
        );
        return rows ?? [];
    }

    /**
     * Half-open range [start, end) in user-local time (converted server-side to institution time).
     * Pass start at 00:00 and end at 00:00 the following calendar day for a single local day.
     */
    public static async getWorkflowAuditHistoryBySubmissionModDateRange(
        start: Date,
        end: Date,
    ): Promise<IngestionWorkflowAuditRow[]> {
        const isoFmt = S25Dateformat.getISODateTimeFormat();
        const formattedStart = S25Datefilter.transform(start, isoFmt);
        const formattedEnd = S25Datefilter.transform(end, isoFmt);
        const encStart = encodeURIComponent(formattedStart);
        const encEnd = encodeURIComponent(formattedEnd);
        const rows = await DataAccess.get<IngestionWorkflowAuditRow[]>(
            `/ingestion/workflow-audit-by-submission-mod-date.json?startDate=${encStart}&endDate=${encEnd}`,
        );
        return rows ?? [];
    }

    public static async getEventSurveySubmission(submissionIdUuid: string): Promise<IngestionSubmission> {
        return await DataAccess.get<IngestionSubmission>(`/ingestion/event-survey/${submissionIdUuid}.json`);
    }

    public static async getEventSubmission(submissionIdUuid: string): Promise<IngestionSubmission> {
        return await DataAccess.get<IngestionSubmission>(`/ingestion/event-submission/${submissionIdUuid}.json`);
    }

    public static async getMySubmissions(): Promise<IngestionSubmission[]> {
        return (await DataAccess.get<IngestionSubmission[]>("/ingestion/my-submissions.json")) || [];
    }

    /**
     * Lightweight check: does the current user have submissions needing attention?
     * (REQUESTED_CHANGES or unsubmitted spawned child forms)
     */
    public static hasSubmissionsNeedingAttention(): Promise<boolean> {
        return DataAccess.get<boolean>("/ingestion/my-incomplete-submissions.json");
    }

    // Answer Management
    public static async saveAnswersForField(
        submissionIdUuid: string,
        fieldDefinitionId: number,
        answers: IngestionFieldAnswer[],
    ): Promise<void> {
        return await DataAccess.post<void>(
            `/ingestion/submission/${submissionIdUuid}/field/${fieldDefinitionId}/answers.json`,
            answers,
        );
    }

    public static async saveAnswers(submissionIdUuid: string, answers: IngestionFieldAnswer[]): Promise<void> {
        return await DataAccess.post<void>(`/ingestion/submission/${submissionIdUuid}/answers.json`, answers);
    }

    public static async getAnswers(submissionIdUuid: string): Promise<IngestionFieldAnswer[]> {
        return (
            (await DataAccess.get<IngestionFieldAnswer[]>(`/ingestion/submission/${submissionIdUuid}/answers.json`)) ||
            []
        );
    }

    // Submission
    public static async submitSubmission(submissionIdUuid: string): Promise<void> {
        return await DataAccess.post<void>(`/ingestion/submission/${submissionIdUuid}/submit.json`, {});
    }

    public static async editSubmittedAnswers(
        submissionIdUuid: string,
        answers: IngestionFieldAnswer[],
    ): Promise<{ message: string; messageCode: string }> {
        return await DataAccess.put<{ message: string; messageCode: string }>(
            `/ingestion/submission/${submissionIdUuid}/edit-answers.json`,
            answers,
        );
    }

    public static async deleteSubmission(submissionIdUuid: string): Promise<void> {
        return await DataAccess.delete<void>(`/ingestion/submission/${submissionIdUuid}.json`);
    }

    private static attachFieldDefinitionsToAnswers(
        answers: IngestionFieldAnswer[],
        fieldDefinitions: IngestionFieldDefinition[],
    ): void {
        const definitionById = new Map<number, IngestionFieldDefinition>();
        for (const fieldDefinition of fieldDefinitions) {
            if (fieldDefinition.fieldDefinitionId != null) {
                definitionById.set(fieldDefinition.fieldDefinitionId, fieldDefinition);
            }
        }
        for (const answer of answers) {
            if (!answer.fieldDefinition && answer.fieldDefinitionId != null) {
                const matched = definitionById.get(answer.fieldDefinitionId);
                if (matched) {
                    answer.fieldDefinition = matched;
                }
            }
        }
    }

    /**
     * Create a new draft on {@code targetIngestionPointId} and pre-fill answers from {@code sourceSubmissionIdUuid}
     * using client-side tiered field matching (see {@link matchSourceAnswersToTargetFields}).
     */
    public static async forkSubmissionToNewDraft(options: {
        sourceSubmissionIdUuid: string;
        targetIngestionPointId: number;
    }): Promise<IngestionSubmission> {
        const sourceSubmission = await this.getSubmission(options.sourceSubmissionIdUuid);
        if (!sourceSubmission?.ingestionPointId || !sourceSubmission.submissionIdUuid) {
            throw new Error("Source submission not found.");
        }
        const submitterContactId = sourceSubmission.contId;
        if (submitterContactId == null) {
            throw new Error("Source submission has no submitter.");
        }

        const [sourceAnswers, sourceFieldDefinitions, targetFieldDefinitions] = await Promise.all([
            this.getAnswers(options.sourceSubmissionIdUuid),
            this.getFieldDefinitions(sourceSubmission.ingestionPointId),
            this.getFieldDefinitions(options.targetIngestionPointId),
        ]);
        this.attachFieldDefinitionsToAnswers(sourceAnswers, sourceFieldDefinitions);

        const mappedAnswers = matchSourceAnswersToTargetFields(
            sourceAnswers,
            targetFieldDefinitions,
            options.targetIngestionPointId,
            submitterContactId,
        );

        const createPayload: Partial<IngestionSubmission> = {};
        if (options.targetIngestionPointId === sourceSubmission.ingestionPointId && sourceSubmission.eventId != null) {
            createPayload.eventId = sourceSubmission.eventId;
        }

        const createdSubmission = await this.createSubmission(options.targetIngestionPointId, createPayload);
        if (!createdSubmission.submissionIdUuid) {
            throw new Error("Could not create draft submission.");
        }
        if (mappedAnswers.length) {
            await this.saveAnswers(createdSubmission.submissionIdUuid, mappedAnswers);
        }
        return await this.getSubmission(createdSubmission.submissionIdUuid);
    }

    /**
     * Get names for event types, organizations, contacts, roles, and categories
     * This method handles the specific object types used in ingestion forms
     */
    public static async getObjectNames(
        objectType: NameResolvableFieldType | "EVENT_ROLE",
        objectIds: number[],
    ): Promise<{ [key: string]: string }> {
        if (objectIds.length === 0) {
            return {};
        }

        try {
            if (objectType === "EVENT_TYPE") {
                // Use EventTypeService to get event type names
                const eventTypes = await EventTypeService.getSingleton().get();
                const nameMap: { [key: string]: string } = {};
                eventTypes.forEach((eventType: EventType | undefined) => {
                    if (eventType && objectIds.includes(eventType.type_id)) {
                        nameMap[`${eventType.type_id}-EVENT_TYPE`] = eventType.type_name;
                    }
                });
                return nameMap;
            } else if (objectType === "EVENT") {
                // Use NameService to get event names
                const eventItems = objectIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Event }));
                const namesMap = await NameService.getNames(eventItems);
                const nameMap: { [key: string]: string } = {};
                objectIds.forEach((id) => {
                    const key = `${id}-${Item.Ids.Event}`;
                    const name = namesMap[key];
                    if (name) {
                        nameMap[`${id}-EVENT`] = name;
                    }
                });
                return nameMap;
            } else if (objectType === "ORGANIZATION") {
                // Use OrganizationService to get organization names
                const organizations = await OrganizationService.getOrganizationsMinimal(objectIds);
                const nameMap: { [key: string]: string } = {};
                organizations.forEach((item) => {
                    if (item.itemName) {
                        nameMap[`${item.itemId}-ORGANIZATION`] = item.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "CONTACT") {
                // Use ContactService to get contact names
                const contacts = await ContactService.getContactsNameAndFormal(objectIds);
                const nameMap: { [key: string]: string } = {};
                contacts.forEach((item) => {
                    if (item.itemName) {
                        nameMap[`${item.itemId}-CONTACT`] = item.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "ROLE") {
                // Use SearchCriteriaService to get role names
                const roles = await SearchCriteriaService.getOrganizationRoles(true, true);
                const nameMap: { [key: string]: string } = {};
                roles.forEach((role: DropDownItem) => {
                    if (!role.itemId || !role.itemName) return;
                    const roleId = typeof role.itemId === "number" ? role.itemId : Number(role.itemId);
                    if (!isNaN(roleId) && objectIds.includes(roleId)) {
                        nameMap[`${roleId}-ROLE`] = role.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "EVENT_ROLE") {
                const roles = await SearchCriteriaService.getEventRoles(false, false);
                const nameMap: { [key: string]: string } = {};
                roles.forEach((role: DropDownItem) => {
                    if (!role.itemId || !role.itemName) return;
                    const roleId = typeof role.itemId === "number" ? role.itemId : Number(role.itemId);
                    if (!isNaN(roleId) && objectIds.includes(roleId)) {
                        nameMap[`${roleId}-EVENT_ROLE`] = role.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "ORG_TYPE") {
                // Use SearchCriteriaService to get organization type names
                const organizationTypes = await SearchCriteriaService.getOrganizationTypes();
                const nameMap: { [key: string]: string } = {};
                organizationTypes.forEach((type: DropDownItem) => {
                    if (!type.itemId || !type.itemName) return;
                    const typeId = typeof type.itemId === "number" ? type.itemId : Number(type.itemId);
                    if (!isNaN(typeId) && objectIds.includes(typeId)) {
                        nameMap[`${typeId}-ORG_TYPE`] = type.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "ORG_RATING") {
                const ratings = await SearchCriteriaService.getOrganizationRatings();
                const nameMap: { [key: string]: string } = {};
                ratings.forEach((rating: DropDownItem) => {
                    if (!rating.itemId || !rating.itemName) return;
                    const ratingId = typeof rating.itemId === "number" ? rating.itemId : Number(rating.itemId);
                    if (!isNaN(ratingId) && objectIds.includes(ratingId)) {
                        nameMap[`${ratingId}-ORG_RATING`] = rating.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "SECURITY_GROUP") {
                const groups = await SearchCriteriaService.getSecurityGroups();
                const nameMap: { [key: string]: string } = {};
                groups.forEach((group: DropDownItem) => {
                    if (!group.itemId || !group.itemName) return;
                    const groupId = typeof group.itemId === "number" ? group.itemId : Number(group.itemId);
                    if (!isNaN(groupId) && objectIds.includes(groupId)) {
                        nameMap[`${groupId}-SECURITY_GROUP`] = group.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "CATEGORIES") {
                // Use SearchCriteriaService to get category names
                const categories = await SearchCriteriaService.getEventCategories();
                const nameMap: { [key: string]: string } = {};
                categories.forEach((cat: DropDownItem) => {
                    if (!cat.itemId || !cat.itemName) return;
                    const catId = typeof cat.itemId === "number" ? cat.itemId : Number(cat.itemId);
                    if (!isNaN(catId) && objectIds.includes(catId)) {
                        nameMap[`${catId}-CATEGORIES`] = cat.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "REQUIREMENTS") {
                const requirements = await SearchCriteriaService.getEventRequirements(false);
                const nameMap: { [key: string]: string } = {};
                requirements.forEach((req: DropDownItem) => {
                    if (!req.itemId || !req.itemName) return;
                    const reqId = typeof req.itemId === "number" ? req.itemId : Number(req.itemId);
                    if (!isNaN(reqId) && objectIds.includes(reqId)) {
                        nameMap[`${reqId}-REQUIREMENTS`] = req.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "ORGANIZATION_MULTISELECT") {
                const organizations = await OrganizationService.getOrganizationsMinimal(objectIds);
                const nameMap: { [key: string]: string } = {};
                organizations.forEach((item) => {
                    if (item.itemName) {
                        nameMap[`${item.itemId}-ORGANIZATION_MULTISELECT`] = item.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "ORG_CATEGORIES") {
                const categories = await SearchCriteriaService.getOrganizationCategories();
                const nameMap: { [key: string]: string } = {};
                categories.forEach((cat: DropDownItem) => {
                    if (!cat.itemId || !cat.itemName) return;
                    const catId = typeof cat.itemId === "number" ? cat.itemId : Number(cat.itemId);
                    if (!isNaN(catId) && objectIds.includes(catId)) {
                        nameMap[`${catId}-ORG_CATEGORIES`] = cat.itemName;
                    }
                });
                return nameMap;
            } else if (objectType === "ELECTION_POSITION") {
                const items = await SearchCriteriaService.getElectionPositions(objectIds);
                const nameMap: { [key: string]: string } = {};
                for (const row of items) {
                    if (row.itemId != null && !Number.isNaN(Number(row.itemId))) {
                        nameMap[`${row.itemId}-ELECTION_POSITION`] = row.itemName ?? "";
                    }
                }
                return nameMap;
            }
        } catch (error) {
            console.error(`Error loading ${objectType} names:`, error);
        }

        return {};
    }

    // Search and Reporting
    public static async getDistinctReportingFields(
        ingestionPointIds: number[],
        searchAllForms: boolean,
        includeDeleted = false,
    ): Promise<ReportingFieldInfo[]> {
        const request: DistinctFieldsRequest = {
            ingestionPointIds,
            searchAllForms,
            includeDeleted,
        };
        return await DataAccess.post<ReportingFieldInfo[]>(`/ingestion/search-report/distinct-fields.json`, {
            DistinctFieldsRequest: request,
        });
    }

    public static async getIngestionPointsForSearchReport(): Promise<IngestionPoint[]> {
        return await DataAccess.get<IngestionPoint[]>(`/ingestion/search-report/ingestion-points.json`);
    }

    public static async getIngestionPointsByIds(ingestionPointIds: number[]): Promise<IngestionPoint[]> {
        if (!ingestionPointIds || ingestionPointIds.length === 0) {
            return [];
        }
        return await DataAccess.post<IngestionPoint[]>(
            `/ingestion/search-report/ingestion-points-by-ids.json`,
            ingestionPointIds,
        );
    }

    public static async executeSearch(request: SearchRequest): Promise<SearchResponse> {
        return await DataAccess.post<SearchResponse>(`/ingestion/search-report/search.json`, {
            SearchRequest: request,
        });
    }

    public static async executeReport(request: ReportRequest): Promise<ReportResponse> {
        return await DataAccess.post<ReportResponse>(`/ingestion/search-report/report.json`, {
            ReportRequest: request,
        });
    }

    public static async saveSearch(name: string, searchRequest: SearchRequest): Promise<IngestionSavedQuery> {
        return await DataAccess.post<IngestionSavedQuery>(`/ingestion/search-report/save-search.json`, {
            SaveSearchRequest: {
                name,
                searchRequest,
            },
        });
    }

    public static async saveReport(name: string, reportRequest: ReportRequest): Promise<IngestionSavedQuery> {
        return await DataAccess.post<IngestionSavedQuery>(`/ingestion/search-report/save-report.json`, {
            SaveReportRequest: {
                name,
                reportRequest,
            },
        });
    }

    public static async getSavedSearches(): Promise<IngestionSavedQuery[]> {
        return await DataAccess.get<IngestionSavedQuery[]>(`/ingestion/search-report/saved-searches.json`);
    }

    public static async getSavedReports(): Promise<IngestionSavedQuery[]> {
        return await DataAccess.get<IngestionSavedQuery[]>(`/ingestion/search-report/saved-reports.json`);
    }

    public static async getSavedQueries(): Promise<IngestionSavedQuery[]> {
        // Combine saved searches and reports
        const [searches, reports] = await Promise.all([this.getSavedSearches(), this.getSavedReports()]);
        return [...searches, ...reports];
    }

    public static async deleteSavedQuery(queryId: number): Promise<void> {
        return await DataAccess.delete<void>(`/ingestion/search-report/saved-query/${queryId}.json`);
    }

    public static async getSavedQuery(queryId: number): Promise<SavedQueryWithData> {
        return await DataAccess.get<SavedQueryWithData>(`/ingestion/search-report/saved-query/${queryId}.json`);
    }

    public static async updateSavedSearch(
        queryId: number,
        name: string,
        searchRequest: SearchRequest,
    ): Promise<IngestionSavedQuery> {
        return await DataAccess.put<IngestionSavedQuery>(
            `/ingestion/search-report/saved-query/${queryId}/search.json`,
            {
                SaveSearchRequest: {
                    name,
                    searchRequest,
                },
            },
        );
    }

    public static async updateSavedReport(
        queryId: number,
        name: string,
        reportRequest: ReportRequest,
    ): Promise<IngestionSavedQuery> {
        return await DataAccess.put<IngestionSavedQuery>(
            `/ingestion/search-report/saved-query/${queryId}/report.json`,
            {
                SaveReportRequest: {
                    name,
                    reportRequest,
                },
            },
        );
    }

    public static isJsonField(fieldDef: IngestionFieldDefinition): boolean {
        return !!fieldDef.fieldType && JSON_FIELD_TYPES.has(fieldDef.fieldType);
    }
}
